import { FC } from 'react';
import moment from 'moment';

import { useIsMobile } from '@/hooks/use-mobile';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { useGlobalStore } from '@/stores/globalStore';

import FileSelectionHeaderMobile from './FileSelectionHeaderMobile';
import { ImageState } from './ImageCompareDialog';
import { ImageCompareSlider } from './ImageCompareSlider';
import ImageDisplay from './ImageDisplay';

interface ComparisonViewProps {
  leftImage: ImageState | null;
  rightImage: ImageState | null;
  onLeftTransform: (transform: { scale: number; rotation: number }) => void;
  onRightTransform: (transform: { scale: number; rotation: number }) => void;
  onLeftSelect: () => void;
  onRightSelect: () => void;
}

const ComparisonView: FC<ComparisonViewProps> = ({
  leftImage,
  rightImage,
  onLeftTransform,
  onRightTransform,
  onLeftSelect,
  onRightSelect,
}) => {
  const { users } = useGlobalStore();
  const isMobile = useIsMobile();

  const getUserFullName = (uuid: string) => {
    const user = users.find((user) => user.uuid === uuid);
    if (!user) return '-';
    const first = user.first_name || '';
    const last = user.last_name || '';
    const full = `${first} ${last}`.trim();
    return full || '-';
  };

  return (
    <Tabs
      defaultValue="side-by-side"
      className="flex flex-1 flex-col justify-between sm:block"
    >
      <TabsList className="order-last mx-auto mt-4 grid h-auto w-full grid-cols-3 bg-blue-200 sm:order-none sm:mb-4 sm:mt-0">
        <TabsTrigger
          value="side-by-side"
          className="font-montserrat w-full outline-none data-[state=active]:text-primary data-[state=active]:after:hidden"
        >
          Розділення
        </TabsTrigger>
        <TabsTrigger
          value="slider"
          className="font-montserrat w-full outline-none data-[state=active]:text-primary data-[state=active]:after:hidden"
        >
          Слайдер
        </TabsTrigger>
        <TabsTrigger
          value="metadata"
          className="font-montserrat w-full outline-none data-[state=active]:text-primary data-[state=active]:after:hidden"
        >
          Метадані
        </TabsTrigger>
      </TabsList>

      <TabsContent
        value="side-by-side"
        className="mt-0 flex flex-grow flex-col gap-1 sm:flex-row sm:gap-4"
      >
        {isMobile && (
          <FileSelectionHeaderMobile
            onLeftSelect={onLeftSelect}
            onRightSelect={onRightSelect}
            leftFilename={leftImage?.name}
            rightFilename={rightImage?.name}
          />
        )}
        {leftImage && (
          <div className="order-2 flex h-full max-h-[80vh] flex-1 items-center justify-center overflow-hidden">
            <ImageDisplay
              image={leftImage}
              onTransform={onLeftTransform}
              isFirst
            />
          </div>
        )}
        {rightImage && (
          <div className="order-4 flex h-full max-h-[80vh] flex-1 items-center justify-center overflow-hidden">
            <ImageDisplay image={rightImage} onTransform={onRightTransform} />
          </div>
        )}
      </TabsContent>

      <TabsContent
        value="slider"
        className="mt-0 flex flex-col justify-between sm:block sm:h-[600px]"
      >
        {isMobile && (
          <FileSelectionHeaderMobile
            onLeftSelect={onLeftSelect}
            onRightSelect={onRightSelect}
            leftFilename={leftImage?.name}
            rightFilename={rightImage?.name}
          />
        )}
        {leftImage && rightImage && (
          <ImageCompareSlider leftImage={leftImage} rightImage={rightImage} />
        )}
      </TabsContent>

      <TabsContent
        value="metadata"
        className="mt-0 flex flex-grow flex-col justify-between sm:block"
      >
        {isMobile && (
          <FileSelectionHeaderMobile
            onLeftSelect={onLeftSelect}
            onRightSelect={onRightSelect}
            leftFilename={leftImage?.name}
            rightFilename={rightImage?.name}
          />
        )}
        {leftImage && rightImage && (
          <div className="order-2 grid h-full grid-cols-2 gap-4 sm:order-none sm:h-auto sm:grid-cols-[450px_1fr_1fr_450px] sm:gap-8 sm:p-6">
            <div className="flex aspect-square max-h-[50vh] items-center justify-center overflow-hidden">
              <img
                src={leftImage.webp_url || leftImage.file_link || ''}
                alt={leftImage.description || leftImage.name || 'image'}
                loading="lazy"
                decoding="async"
                className="h-full w-full rounded-md object-contain"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  if (leftImage.webp_url && target.src !== (leftImage.file_link || '')) {
                    target.src = leftImage.file_link || '';
                  }
                }}
              />
            </div>

            <div className="space-y-4">
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Формат</h3>
                <p className="text-gray-600">
                  {leftImage.format?.toUpperCase() ?? '-'}
                </p>
              </div>
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Розмір</h3>
                <p className="text-gray-600">{leftImage.size}</p>
              </div>
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Розширення</h3>
                <p className="text-gray-600">
                  {leftImage.width && leftImage.height
                    ? `${leftImage.width}x${leftImage.height}`
                    : '-'}
                </p>
              </div>
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Дата зйомки</h3>
                <p className="text-gray-600">
                  {leftImage.created
                    ? moment(leftImage.created).format('DD.MM.YYYY')
                    : '-'}
                </p>
              </div>
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Автор</h3>
                <p className="text-gray-600">
                  {getUserFullName(leftImage.user)}
                </p>
              </div>
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Координати</h3>
                <p className="text-gray-600">
                  {leftImage.gps_latitude && leftImage.gps_longitude
                    ? `${leftImage.gps_latitude}, ${leftImage.gps_longitude}`
                    : '-'}
                </p>
              </div>
            </div>
            {/* switch last two elements when is mobile */}
            <div className="order-last space-y-4 sm:order-none sm:justify-self-end">
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Формат</h3>
                <p
                  className={`${rightImage.format !== leftImage.format ? 'text-destructive' : 'text-gray-600 '}`}
                >
                  {rightImage.format?.toUpperCase() ?? '-'}
                </p>
              </div>
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Розмір</h3>
                <p
                  className={`${rightImage.size !== leftImage.size ? 'text-destructive' : 'text-gray-600 '}`}
                >
                  {rightImage.size ?? '-'}
                </p>
              </div>
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Розширення</h3>
                {rightImage.width && rightImage.height ? (
                  <p
                    className={`${
                      rightImage.width !== leftImage.width ||
                      rightImage.height !== leftImage.height
                        ? 'text-destructive'
                        : 'text-gray-600 '
                    }`}
                  >
                    {rightImage.width}x{rightImage.height}
                  </p>
                ) : (
                  <p className="text-gray-600">-</p>
                )}
              </div>
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Дата зйомки</h3>
                <p
                  className={`${
                    moment(rightImage.created).isSame(moment(leftImage.created))
                      ? 'text-gray-600 '
                      : 'text-destructive'
                  }`}
                >
                  {rightImage.created
                    ? moment(rightImage.created).format('DD.MM.YYYY')
                    : '-'}
                </p>
              </div>
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Автор</h3>
                <p
                  className={`${rightImage.user !== leftImage.user ? 'text-destructive' : 'text-gray-600 '}`}
                >
                  {getUserFullName(rightImage.user)}
                </p>
              </div>
              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Координати</h3>
                {rightImage.gps_latitude && rightImage.gps_longitude ? (
                  <p
                    className={`${
                      rightImage.gps_latitude !== leftImage.gps_latitude ||
                      rightImage.gps_longitude !== leftImage.gps_longitude
                        ? 'text-destructive'
                        : 'text-gray-600 '
                    }`}
                  >
                    {rightImage.gps_latitude}, {rightImage.gps_longitude}
                  </p>
                ) : (
                  <p className="text-gray-600">-</p>
                )}
              </div>
            </div>
            <div className="flex aspect-square max-h-[50vh] items-center justify-center overflow-hidden">
              <img
                src={rightImage.webp_url || rightImage.file_link || ''}
                alt={rightImage.description || rightImage.name || 'image'}
                loading="lazy"
                decoding="async"
                className="h-full w-full rounded-md object-contain"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  if (rightImage.webp_url && target.src !== (rightImage.file_link || '')) {
                    target.src = rightImage.file_link || '';
                  }
                }}
              />
            </div>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
};

export default ComparisonView;
